export type ClimateZone = 'B' | 'C' | 'D' | 'E' | 'F';

type TempPair = [number, number];

export const ZONE_BY_PREFIX: Record<string, ClimateZone> = {
  '00': 'D', '01': 'D', '02': 'D', '03': 'D', '04': 'C', '05': 'D', '06': 'E', '07': 'C', '08': 'D', '09': 'C',
  '10': 'E', '11': 'F', '12': 'E', '13': 'E', '14': 'E', '15': 'E', '16': 'D', '17': 'D', '18': 'D', '19': 'D',
  '20': 'E', '21': 'E', '22': 'E', '23': 'F', '24': 'E', '25': 'E', '26': 'E', '27': 'E', '28': 'E', '29': 'E',
  '30': 'E', '31': 'E', '32': 'F', '33': 'E', '34': 'E', '35': 'E', '36': 'E', '37': 'E', '38': 'F', '39': 'F',
  '40': 'E', '41': 'E', '42': 'E', '43': 'E', '44': 'E', '45': 'E', '46': 'E', '47': 'E', '48': 'E',
  '50': 'D', '51': 'D', '52': 'E', '53': 'D', '54': 'D', '55': 'D', '56': 'D', '57': 'D', '58': 'D', '59': 'D',
  '60': 'E', '61': 'E', '62': 'E', '63': 'D', '64': 'D', '65': 'D', '66': 'D', '67': 'E',
  '70': 'C', '71': 'C', '72': 'C', '73': 'C', '74': 'C', '75': 'D', '76': 'C',
  '80': 'C', '81': 'C', '82': 'D', '83': 'D', '84': 'C', '85': 'D', '86': 'E', '87': 'C', '88': 'C', '89': 'B',
  '90': 'B', '91': 'B', '92': 'B', '93': 'B', '94': 'C', '95': 'B', '96': 'B', '97': 'B', '98': 'B',
};

/** Netto Conto Termico in euro. Per taglia: [bassa, media]. Bassa = pavimento. Media = termosifoni e ventole.
 *  Righe storiche, in ordine: fino a 110, 110-170, 170-230, oltre 230.
 *  Le risposte attuali non seguono quell'indice: fino a 90 usa la riga 1,
 *  90-150 unisce le righe 2 e 3, oltre 150 usa la riga 4 (oltre 230). */
const BONUS: Array<Record<ClimateZone, TempPair>> = [
  { B: [2742, 2025], C: [3549, 2621], D: [4517, 3335], E: [5485, 4050], F: [5807, 4288] },
  { B: [3900, 3145], C: [5047, 4070], D: [6424, 5180], E: [7801, 6290], F: [8259, 6660] },
  { B: [4133, 3357], C: [5349, 4345], D: [6808, 5529], E: [8267, 6714], F: [8753, 7109] },
  { B: [4549, 3544], C: [5887, 4586], D: [7492, 5837], E: [9098, 7088], F: [9633, 7505] },
];

/** Indice della riga «oltre 230». Il tetto di zona deve restare su questa riga. */
const ROW_OLTRE_230 = 3;

export const SIZE_OPTIONS = [
  { value: 'fino a 90', label: 'Fino a 90 mq' },
  { value: '90-150', label: '90-150 mq' },
  { value: 'oltre 150', label: 'oltre i 150 mq' },
] as const;

const WHEN_LABEL: Record<string, string> = {
  one_to_two_months: '1-2 mesi',
  three_to_four_months: '3-4 mesi',
  more_than_5_months: 'più di 5 mesi',
};

export const HEAT_LABEL: Record<string, string> = {
  termosifoni: 'Termosifoni',
  pavimento: 'Pavimento radiante',
  ventole: 'Fancoil',
};

export function zoneFromCap(cap: string): ClimateZone | null {
  if (!/^\d{5}$/.test(cap)) return null;
  return ZONE_BY_PREFIX[cap.slice(0, 2)] || null;
}

export function formatEur(value: number): string {
  const formatted = Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return formatted + ' €';
}

export function formatRange(low: number, high: number): string {
  if (low === high) return formatEur(low);
  return formatEur(low) + ' – ' + formatEur(high);
}

function floorOf(zone: ClimateZone, low: boolean, row: number): number | null {
  const pair = BONUS[row]?.[zone];
  if (!pair) return null;
  const amount = pair[low ? 0 : 1];
  return Number.isFinite(amount) ? amount : null;
}

/**
 * Righe storiche usate da una risposta. Valori diversi dai tre attuali
 * (sessione vecchia, campo vuoto) non indicizzano la tabella.
 */
function rowsForSize(sizeValue?: string): number[] | null {
  if (sizeValue === 'fino a 90') return [0];
  if (sizeValue === '90-150') return [1, 2];
  if (sizeValue === 'oltre 150') return [ROW_OLTRE_230];
  return null;
}

export function zoneMax(zone: ClimateZone): number {
  return BONUS[ROW_OLTRE_230][zone][0];
}

/** Estremi già presenti nella tabella Conto Termico, senza ricalcolo. */
export function zoneBounds(zone: ClimateZone): { min: number; max: number } {
  let min = Infinity;
  let max = 0;
  BONUS.forEach((row) => {
    const pair = row[zone];
    min = Math.min(min, pair[0], pair[1]);
    max = Math.max(max, pair[0], pair[1]);
  });
  return { min, max };
}

/** Estremi della sola colonna impianto (pavimento oppure termosifoni/ventole). */
export function heatBounds(zone: ClimateZone, heat: string): { min: number; max: number } {
  const column = heat === 'pavimento' ? 0 : 1;
  let min = Infinity;
  let max = 0;
  BONUS.forEach((row) => {
    const value = row[zone][column];
    min = Math.min(min, value);
    max = Math.max(max, value);
  });
  return { min, max };
}

/**
 * Stringe la fascia verso il centro, restando dentro gli estremi possibili.
 * Ogni passo tiene il 92% della larghezza (circa −8%).
 */
export function shrinkTowardCenter(
  min: number,
  max: number,
  steps: number
): { min: number; max: number } {
  if (steps <= 0 || max <= min) {
    return { min: Math.round(min), max: Math.round(max) };
  }
  const factor = 0.92 ** steps;
  const center = (min + max) / 2;
  const half = ((max - min) * factor) / 2;
  const nextMin = Math.max(min, Math.round(center - half));
  const nextMax = Math.min(max, Math.round(center + half));
  if (nextMax <= nextMin) {
    return { min: Math.round(min), max: Math.round(max) };
  }
  return { min: nextMin, max: nextMax };
}

function split4(total: number): number[] {
  const weights = [8, 3, 5, 13];
  const sumW = weights.reduce((sum, weight) => sum + weight, 0);
  const rounded = Math.round(total);
  const parts = weights.map((weight) => Math.floor((rounded * weight) / sumW));
  parts[parts.length - 1] += rounded - parts.reduce((sum, part) => sum + part, 0);
  return parts;
}

export interface ValuationQuote {
  c1: number;
  c2: number;
  c3: number | null;
  value: number | null;
  high: number | null;
}

export function quote(zone: ClimateZone, heat?: string, sizeValue?: string): ValuationQuote {
  const floor = floorOf(zone, false, 0);
  if (floor === null) {
    return { c1: 0, c2: 0, c3: null, value: null, high: null };
  }
  const [c1, c2, c3base] = split4(floor);
  if (!heat) {
    return { c1, c2, c3: null, value: null, high: null };
  }
  const low = heat === 'pavimento';
  const heatedFloor = floorOf(zone, low, 0);
  if (heatedFloor === null) {
    return { c1, c2, c3: null, value: null, high: null };
  }
  const c3 = c3base + Math.max(0, heatedFloor - floor);
  const rows = rowsForSize(sizeValue);
  if (!rows) {
    return { c1, c2, c3, value: null, high: null };
  }
  const amounts: number[] = [];
  for (const row of rows) {
    const amount = floorOf(zone, low, row);
    if (amount === null) {
      return { c1, c2, c3, value: null, high: null };
    }
    amounts.push(amount);
  }
  if (amounts.length === 1) {
    const value = amounts[0];
    const high = Math.min(value + 1500, zoneMax(zone));
    return { c1, c2, c3, value, high };
  }
  return {
    c1,
    c2,
    c3,
    value: Math.min(...amounts),
    high: Math.max(...amounts),
  };
}

export function bonificoOf(value: number, high: number): number {
  return Math.round(((value + high) / 2) * 0.65);
}

export function bonificoBand(value: number, high: number): { low: number; high: number } {
  const base = bonificoOf(value, high);
  return { low: Math.round(base * 0.95), high: Math.round(base * 1.05) };
}

export function ritiroTitle(when?: string): string {
  const label = when ? WHEN_LABEL[when] : undefined;
  return label ? 'Ritiro caldaia in ' + label : 'Ritiro caldaia, senza scadenza precisa';
}
